/**
 * SSO Token Handler — Option C (Firebase Custom Token).
 *
 * Reads `?customToken=...` from the URL (set by the portal's
 * launchAppWithSSO mint flow), exchanges it via signInWithCustomToken,
 * exchanges the resulting Firebase ID token with the EF-Projex backend,
 * and stores the backend JWT expected by AuthContext.
 *
 * Legacy raw token query params are rejected and stripped from the URL.
 */

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { signInWithCustomToken } from 'firebase/auth';
import { auth } from '../lib/firebase';
import axios from 'axios';

const SSOTokenHandler = ({ children }) => {
  const navigate = useNavigate();
  const [isAuthenticating, setIsAuthenticating] = useState(false);

  useEffect(() => {
    const handleSSOToken = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const customToken = urlParams.get('customToken');
      const legacyToken = urlParams.get('token') || urlParams.get('sso_token');

      if (!customToken && !legacyToken) return;

      setIsAuthenticating(true);

      const cleanUrl = () => {
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${window.location.hash}`
        );
      };

      // Strip single-use credentials before any network request.
      cleanUrl();

      // ---- Firebase custom token ----
      if (customToken) {
        console.log('🔐 SSO custom token detected — signing in via Firebase...');
        try {
          if (!auth) throw new Error('Firebase auth not initialized');
          const result = await signInWithCustomToken(auth, customToken);


          const idToken = await result.user.getIdToken(true);
          const response = await axios.post('/api/v1/google', {
            idToken,
            email: result.user.email || '',
            displayName: result.user.displayName || result.user.email || '',
            uid: result.user.uid,
          });

          const { access_token, token_type } = response.data || {};
          if (!access_token) {
            throw new Error('Backend returned no access_token');
          }

          localStorage.setItem('auth_token', access_token);
          localStorage.setItem('token_type', token_type || 'bearer');
          cleanUrl();
          window.location.href = '/dashboard';
        } catch (error) {
          console.warn('EF-Projex SSO failed');
          cleanUrl();
          const errorMsg = error.response?.data?.detail || error.message || 'Failed to sign in via SSO.';
          alert(`SSO Error: ${errorMsg}`);
          navigate('/auth/login', { replace: true });
        } finally {
          setIsAuthenticating(false);
        }
        return;
      }

      console.warn('Rejected legacy raw-token SSO link.');
      cleanUrl();
      alert('This SSO link has expired. Please launch EF-Projex again from the portal.');
      navigate('/auth/login', { replace: true });
      setIsAuthenticating(false);
    };

    handleSSOToken();
  }, [navigate]);

  if (isAuthenticating) {
    return (
      <div className="flex flex-col items-center justify-center h-screen gap-4 bg-gray-50">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-indigo-600"></div>
        <h2 className="text-xl font-semibold text-gray-700">Authenticating via Single Sign-On...</h2>
        <p className="text-gray-500">Please wait while we verify your credentials</p>
      </div>
    );
  }

  return <>{children}</>;
};

export default SSOTokenHandler;
